import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { CurrentUser, LoginRequest } from "@iso27001/shared";
import { authApi } from "../api/auth.js";
import { ApiError } from "../api/client.js";
import { onSessionExpiry, onSessionDeadline } from "../api/session-expiry.js";
import { applyUserLocale } from "../i18n/index.js";

interface AuthContextValue {
  user: CurrentUser | null;
  loading: boolean;
  /** True when the last sign-out was the session lapsing rather than a click. */
  sessionExpired: boolean;
  login: (body: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthCtx = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["me"],
    queryFn: async (): Promise<CurrentUser | null> => {
      try {
        return await authApi.me();
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
  });

  // The session is the authority on language once it is known; before that the
  // browser decided.
  //
  // `loading` (below) stays true until this has actually finished, not just
  // been scheduled. RequireAuth and PublicDocsLayout both unblock the shell
  // the instant `loading` goes false, which used to be the same render as
  // `data.locale` first arriving — a plain effect only applies the locale
  // *after* that render commits and paints, so a signed-in user with no
  // local preference stored yet (a new device, or cleared storage) briefly
  // saw the whole shell in the module-init language before it flipped to
  // their account's. Blocking on completion instead of firing the effect
  // and moving on is deterministic: it doesn't depend on this effect,
  // i18next's own async changeLanguage, and React's commit/paint all racing
  // in the right order, it just guarantees the gate cannot open early.
  const [localeApplied, setLocaleApplied] = useState(false);
  useEffect(() => {
    if (isLoading) return;
    let cancelled = false;
    void applyUserLocale(data?.locale ?? null).finally(() => {
      if (!cancelled) setLocaleApplied(true);
    });
    return () => {
      cancelled = true;
    };
  }, [isLoading, data?.locale]);

  // Told by apiFetch, centrally, the moment any authenticated call comes back
  // 401 (see api/session-expiry.ts for the two 401s that are NOT this).
  // Clearing the cached user is the whole mechanism: `user` goes null,
  // RequireAuth routes to /login carrying the current location, and LoginPage
  // sends the reader back there afterwards. Nothing new in the router.
  const [sessionExpired, setSessionExpired] = useState(false);

  // Only a *transition* out of a signed-in state is an expiry worth announcing.
  // Without this guard a stray 401 — or a timer left over from a previous
  // session — would raise the banner on a login page somebody navigated to
  // themselves.
  const expireNow = useCallback(() => {
    if (qc.getQueryData(["me"]) == null) return;
    qc.setQueryData(["me"], null);
    setSessionExpired(true);
  }, [qc]);

  // Reactive: any authenticated call coming back 401.
  useEffect(() => onSessionExpiry(expireNow), [expireNow]);

  // Proactive: the server reports the remaining window on every authenticated
  // response, and this waits it out. Without it a tab that is merely open never
  // learns the session ended — nothing here polls, refetchOnWindowFocus is off,
  // so an idle tab makes no requests and kept showing a signed-in UI for a
  // session the server had already rejected.
  //
  // The timer is replaced, not stacked, on each response: every request slides
  // the window server-side, so only the newest deadline is true.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const off = onSessionDeadline((secondsRemaining) => {
      clearTimeout(timer);
      // A second of slack so the timer lands just after the server considers
      // the session over, never a moment before it.
      timer = setTimeout(expireNow, (secondsRemaining + 1) * 1000);
    });
    return () => {
      clearTimeout(timer);
      off();
    };
  }, [expireNow]);

  const loginMut = useMutation({
    mutationFn: (body: LoginRequest) => authApi.login(body),
    onSuccess: ({ user }) => {
      setSessionExpired(false);
      qc.setQueryData(["me"], user);
    },
  });
  const logoutMut = useMutation({
    mutationFn: () => authApi.logout(),
    onSuccess: () => qc.setQueryData(["me"], null),
  });

  const value = useMemo<AuthContextValue>(
    () => ({
      user: data ?? null,
      loading: isLoading || !localeApplied,
      sessionExpired,
      login: async (body) => { await loginMut.mutateAsync(body); },
      logout: async () => { await logoutMut.mutateAsync(); },
    }),
    [data, isLoading, localeApplied, sessionExpired, loginMut, logoutMut],
  );
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthContextValue {
  const v = useContext(AuthCtx);
  if (!v) throw new Error("useAuth must be inside <AuthProvider>");
  return v;
}
